import { baseApi } from "../../../core/data/rtk/baseQuery";
import { ApiResponseType } from "../../../core/data/rtk/apiResponseType";

export interface StarterKitInventory {
    id: number;
    center_id?: number;
    course_id: number;
    course?: {
        id: number;
        name: string;
    };
    is_main_center_stock: boolean;
    total_stock: number;
    available_stock: number;
    center?: {
        id: number;
        name: string;
    };
}

export interface InventoryPayload {
    center_id?: number;
    course_id: number;
    is_main_center_stock?: boolean;
    total_stock: number;
    available_stock: number;
}

export interface TransactionPayload {
    type: 'assigned_to_center' | 'assigned_to_student';
    quantity: number;
    course_id: number;
    from_main_center?: boolean;
    from_center_id?: number;
    to_center_id?: number;
    student_id?: number;
}

export interface StarterKitRequest {
    id: number;
    center: { id: number; name: string; };
    course: { id: number; name: string; };
    quantity: number;
    requested_on: string;
    status: 'pending' | 'approved' | 'rejected' | 'completed';
    reviewer?: { id: number; name: string; };
    reviewer_note?: string;
    reviewed_on?: string;
    created_at: string;
}

export interface StarterKitRequestPayload {
    course_id: number;
    quantity: number;
    center_id?: number;
}

export const starterKitApi = baseApi.injectEndpoints({
    endpoints: (builder) => ({
        // Get All Inventories
        getInventories: builder.query<ApiResponseType<{ items: StarterKitInventory[], totalItems: number }>, any>({
            query: (params) => ({
                url: "starter-kit-inventories/get-all",
                method: "GET",
                params
            }),
            providesTags: ["StarterKitInventory"],
        }),

        // Get All Transactions
        getTransactions: builder.query<ApiResponseType<{ items: any[], totalItems: number }>, any>({
            query: (params) => ({
                url: "starter-kit-transactions/get-all",
                method: "GET",
                params
            }),
            providesTags: ["StarterKitTransaction"],
        }),

        // Save/Create Inventory
        saveInventory: builder.mutation<ApiResponseType<{ inventory: StarterKitInventory }>, InventoryPayload>({
            query: (payload) => ({
                url: "starter-kit-inventories/main-center/save",
                method: "POST",
                body: payload,
            }),
            invalidatesTags: ["StarterKitInventory"],
        }),

        // Save/Create Transaction
        saveTransaction: builder.mutation<ApiResponseType<any>, TransactionPayload>({
            query: (payload) => ({
                url: "starter-kit-transactions/save",
                method: "POST",
                body: payload,
            }),
            invalidatesTags: ["StarterKitTransaction", "StarterKitInventory", "Students", "Centers"],
        }),

        // Add Stock to Main Center
        addMainCenterStock: builder.mutation<ApiResponseType<any>, { quantity: number, course_id: number }>({
            query: (payload) => ({
                url: "starter-kit-inventories/add-main-center-stock",
                method: "POST",
                body: payload,
            }),
            invalidatesTags: ["StarterKitInventory", "StarterKitTransaction", "Centers"],
        }),

        // Update Main Center Inventory (Override)
        updateMainCenterStock: builder.mutation<ApiResponseType<any>, { total_stock: number, available_stock: number, course_id: number }>({
            query: (payload) => ({
                url: "starter-kit-inventories/update-main-center-stock",
                method: "PUT",
                body: payload,
            }),
            invalidatesTags: ["StarterKitInventory", "StarterKitTransaction", "Centers"],
        }),

        // Return Stock to Main Center
        returnToMainCenter: builder.mutation<ApiResponseType<any>, { center_id: number, quantity: number, course_id: number }>({
            query: (payload) => ({
                url: "starter-kit-transactions/return-to-main-center",
                method: "POST",
                body: payload,
            }),
            invalidatesTags: ["StarterKitInventory", "StarterKitTransaction", "Centers"],
        }),

        // Get All Requests
        getRequests: builder.query<ApiResponseType<{ items: StarterKitRequest[], totalItems: number }>, any>({
            query: (params) => ({
                url: "starter-kit-requests/get-all",
                method: "GET",
                params
            }),
            providesTags: ["StarterKitRequest"],
        }),

        // Save Request
        saveRequest: builder.mutation<ApiResponseType<any>, StarterKitRequestPayload>({
            query: (payload) => ({
                url: "starter-kit-requests/save",
                method: "POST",
                body: payload,
            }),
            invalidatesTags: ["StarterKitRequest"],
        }),

        // Update Request Status
        updateRequestStatus: builder.mutation<ApiResponseType<any>, { id: number, status: 'approved' | 'rejected', reviewer_note?: string }>({
            query: ({ id, ...payload }) => ({
                url: `starter-kit-requests/update-status/${id}`,
                method: "PUT",
                body: payload,
            }),
            invalidatesTags: ["StarterKitRequest", "StarterKitInventory", "StarterKitTransaction", "Centers"],
        }),

        // Delete Request
        deleteRequest: builder.mutation<ApiResponseType<any>, number>({
            query: (id) => ({
                url: `starter-kit-requests/delete/${id}`,
                method: "DELETE",
            }),
            invalidatesTags: ["StarterKitRequest"],
        }),
    }),
});

export const { 
    useGetInventoriesQuery,
    useGetTransactionsQuery,
    useSaveInventoryMutation, 
    useSaveTransactionMutation,
    useAddMainCenterStockMutation,
    useUpdateMainCenterStockMutation,
    useReturnToMainCenterMutation,
    useGetRequestsQuery,
    useSaveRequestMutation,
    useUpdateRequestStatusMutation,
    useDeleteRequestMutation
} = starterKitApi;
